/**
 * Derive a single time window from selectionStore `cutEvents` for APIs and client filters.
 * Only uses entries with `start_time` / `end_time` (brush cuts, event time ranges).
 * Plain numeric event-ID cuts are ignored (no bounding times without a separate lookup).
 */

export interface CutBoundingUnixSeconds {
  startSec: number;
  endSec: number;
}

export function getBoundingUnixSecondsFromCutEvents(cutEvents: unknown[]): CutBoundingUnixSeconds | null {
  if (!Array.isArray(cutEvents) || cutEvents.length === 0) {return null;}
  let minStart = Infinity;
  let maxEnd = -Infinity;
  for (const e of cutEvents) {
    if (e == null || typeof e !== 'object') {continue;}
    const o = e as Record<string, unknown>;
    const st = o.start_time;
    const en = o.end_time;
    if (typeof st !== 'string' || typeof en !== 'string') {continue;}
    const s = new Date(st).getTime();
    const endMs = new Date(en).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(endMs)) {continue;}
    minStart = Math.min(minStart, s);
    maxEnd = Math.max(maxEnd, endMs);
  }
  if (minStart === Infinity || maxEnd === -Infinity) {return null;}
  if (maxEnd < minStart) {return null;}
  return {
    startSec: Math.floor(minStart / 1000),
    endSec: Math.floor(maxEnd / 1000),
  };
}

/** Result of combining cut + timeline brush for explore tags/map markers. */
export type ExploreTimelineVisibleBounds =
  | { kind: 'none' }
  | { kind: 'window'; startMs: number; endMs: number }
  | { kind: 'empty' };

/**
 * Millisecond bounds from the first entry in selectionStore `selectedRange`
 * (explore TimeSeries / MapTimeSeries / MultiMapTimeSeries brush).
 */
export function getBoundingMsFromSelectedRange(
  ranges: unknown[] | null | undefined
): { startMs: number; endMs: number } | null {
  if (!Array.isArray(ranges) || ranges.length === 0) {return null;}
  const first = ranges[0];
  if (first == null || typeof first !== 'object') {return null;}
  const o = first as Record<string, unknown>;
  const st = o.start_time;
  const en = o.end_time;
  if (typeof st !== 'string' || typeof en !== 'string') {return null;}
  const s = new Date(st).getTime();
  const e = new Date(en).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e)) {return null;}
  return { startMs: Math.min(s, e), endMs: Math.max(s, e) };
}

/**
 * Intersects optional cut window (from `cutEvents`) with optional brush window (`selectedRange`).
 * Used so tags (and similar) stay aligned with the time window shown on the timeline.
 */
export function getExploreTimelineVisibleBoundsMs(args: {
  applyCut: boolean;
  cutEvents: unknown[];
  brushRanges: unknown[] | null | undefined;
}): ExploreTimelineVisibleBounds {
  const segments: { startMs: number; endMs: number }[] = [];
  if (args.applyCut) {
    const b = getBoundingUnixSecondsFromCutEvents(args.cutEvents);
    if (b) {segments.push({ startMs: b.startSec * 1000, endMs: b.endSec * 1000 });}
  }
  const brush = getBoundingMsFromSelectedRange(args.brushRanges);
  if (brush) {segments.push(brush);}
  if (segments.length === 0) {return { kind: 'none' };}
  let startMs = segments[0].startMs;
  let endMs = segments[0].endMs;
  for (let i = 1; i < segments.length; i++) {
    startMs = Math.max(startMs, segments[i].startMs);
    endMs = Math.min(endMs, segments[i].endMs);
  }
  if (endMs < startMs) {return { kind: 'empty' };}
  return { kind: 'window', startMs, endMs };
}

/**
 * Millisecond bounds for the map playback time window (past N minutes up to reference time).
 * Matches TrackLayer / MultiTrackLayer: `[referenceTime - windowMinutes, referenceTime]`.
 */
export function getPlaybackTimeWindowBoundsMs(args: {
  windowMinutes: number;
  referenceTime: Date | null | undefined;
}): { startMs: number; endMs: number } | null {
  const wm = Number(args.windowMinutes);
  if (!Number.isFinite(wm) || wm <= 0) {return null;}
  const ref = args.referenceTime;
  if (!(ref instanceof Date) || Number.isNaN(ref.getTime())) {return null;}
  const endMs = ref.getTime();
  const startMs = endMs - wm * 60 * 1000;
  if (!Number.isFinite(startMs) || endMs < startMs) {return null;}
  return { startMs, endMs };
}

/**
 * Cut ∩ brush when present; otherwise optional playback map time window.
 * Brush/cut take priority; playback applies when `applyPlaybackWindow` is true and no brush window exists.
 * When playback is inactive, optional fleet race/leg filtered bounds align tags with MapTimeSeries full-mode domain.
 */
export function getExploreVisibleBoundsIncludingPlaybackMs(args: {
  applyCut: boolean;
  cutEvents: unknown[];
  brushRanges: unknown[] | null | undefined;
  applyPlaybackWindow: boolean;
  windowMinutes: number;
  referenceTime: Date | null | undefined;
  fleetFilteredBounds?: { startMs: number; endMs: number } | null;
}): ExploreTimelineVisibleBounds {
  const cutBrush = getExploreTimelineVisibleBoundsMs({
    applyCut: args.applyCut,
    cutEvents: args.cutEvents,
    brushRanges: args.brushRanges,
  });
  if (cutBrush.kind === 'window' || cutBrush.kind === 'empty') {return cutBrush;}
  if (args.applyPlaybackWindow) {
    const playback = getPlaybackTimeWindowBoundsMs({
      windowMinutes: args.windowMinutes,
      referenceTime: args.referenceTime,
    });
    if (playback) {return { kind: 'window', startMs: playback.startMs, endMs: playback.endMs };}
  }
  const fleet = args.fleetFilteredBounds;
  if (
    fleet &&
    Number.isFinite(fleet.startMs) &&
    Number.isFinite(fleet.endMs) &&
    fleet.endMs >= fleet.startMs
  ) {
    return { kind: 'window', startMs: fleet.startMs, endMs: fleet.endMs };
  }
  return { kind: 'none' };
}
